<!-- eslint-disable vue/multi-word-component-names -->
<template>
    <v-container>
        <v-icon @click="back" class="mb-4">mdi-arrow-left</v-icon>
        <h3>Add Product in <span class="text-warning">{{ branchName }}</span> Branch</h3>
        <p class="text-grey my-2"><em>Note: Make sure you are filling out this form with legitimate details</em></p>

        <v-form ref="productForm" @submit.prevent="showSubmitDialog">
            <v-container v-for="(row, index) in productRows" :key="index"
                class="parent border rounded my-3 pb-1 mx-auto">
                <v-btn color="red" class="pe-1 mb-5" style="margin-right: 1000px;" prepend-icon="mdi-trash-can-outline"
                    @click="removeRow(index)">
                </v-btn>

                <v-text-field class="child" v-model="row.productName" label="Product name"
                    :rules="[v => !!v || 'Required']" density="compact" variant="outlined" />

                <v-text-field class="child" v-model="row.basePrice" label="Base price (₱)" type="text"
                    :rules="[v => !isNaN(parseFloat(v)) || 'Required' || 'Must be a valid number']"
                    @input="e => row.basePrice = e.target.value.replace(/[^0-9.]/g, '')" density="compact"
                    variant="outlined" />

                <v-autocomplete class="child" v-model="row.productSize" label="Size" :items="productSizeOption"
                    :rules="[v => !!v || 'Required']" item-title="size_label" item-value="product_size_id"
                    density="compact" variant="outlined" />

                <v-autocomplete class="child" v-model="row.productTemp" label="Temperature"
                    :items="productTemperatureOption" :rules="[v => !!v || 'Required']" item-value="product_temp_id"
                    item-title="temp_label" density="compact" variant="outlined" />

                <v-autocomplete class="child" v-model="row.productCategory" label="Category"
                    :items="productCategoryOption" :rules="[v => !!v || 'Required']"
                    item-value="product_base_category_id" item-title="product_base_category" density="compact"
                    variant="outlined" />

                <v-autocomplete class="child" v-model="row.productStation" label="Station" :items="productStationOption"
                    :rules="[v => !!v || 'Required']" item-value="shop_station_id" item-title="station_name"
                    density="compact" variant="outlined" />

                <!-- Product Image Upload -->
                <div class="image-upload-container child">
                    <div class="image-preview" v-if="row.imagePreview">
                        <img :src="row.imagePreview" alt="Product preview" class="preview-img" />
                        <v-btn size="small" color="error" @click="removeImage(index)" class="remove-image-btn">
                            <v-icon size="small" style="margin-top: 2px;">mdi-close</v-icon>
                        </v-btn>
                    </div>
                    <div v-else class="upload-placeholder" @click="triggerFileUpload(index)">
                        <p class="text-caption">Click to upload product image</p>
                        <p class="text-caption text-grey">Max 2MB • JPG, PNG, WebP</p>
                    </div>
                    <input type="file" :ref="el => { if (el) fileInputRefs[index] = el }"
                        @change="handleFileUpload($event, index)" accept="image/jpeg,image/png,image/webp"
                        style="display: none" />
                </div>

            </v-container>

            <v-row>
                <v-col cols="12">
                    <v-btn color="primary" class="mb-3" prepend-icon="mdi-plus" :disabled="validatingProduct"
                        @click="addRow">
                        Add More
                    </v-btn>
                    <v-btn color="green" class="ms-3 mb-3" prepend-icon="mdi-check"
                        :disabled="!isFormValid || validatingProduct" @click="showSubmitDialog">
                        Submit
                    </v-btn>
                </v-col>
            </v-row>
        </v-form>

        <v-dialog v-model="submitDialog" max-width="500px">
            <v-card>
                <v-card-title>
                    <span class="headline">Confirmation</span>
                </v-card-title>
                <v-card-text>
                    <p class="text-center">Do you want to save new products in <span class="text-warning">{{ branchName
                    }}</span> Branch?</p>
                </v-card-text>
                <v-card-actions class="mx-4 my-4">
                    <v-spacer></v-spacer>
                    <v-btn color="red" variant="flat" class="px-3" prepend-icon="mdi-close"
                        @click="closeSubmitDialog">Check again</v-btn>
                    <v-btn color="green" variant="flat" class="px-3" prepend-icon="mdi-content-save"
                        @click="submitForm">
                        <v-progress-circular v-if="validatingProduct" size="20" color="white" label="Loading..."
                            indeterminate />
                        <span v-else>Save</span>
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <Snackbar ref="snackbarRef" />
        <LoaderUI :visible="validatingProduct" message="Saving products..." />
    </v-container>
</template>

<script>
import { computed } from 'vue';
import Snackbar from '@/components/Snackbar.vue';
import LoaderUI from '@/components/LoaderUI.vue';
import { useProductsStore } from '@/stores/productsStore';
import { useProductOptionsStore } from '@/stores/productOptionsStore';

export default {
    name: 'AddProduct',

    components: {
        Snackbar,
        LoaderUI
    },

    data() {
        return {
            shopID: null,
            branchID: null,
            branchName: null,
            validatingProduct: false,
            submitDialog: false,
            fileInputRefs: {}, // Store file input refs
            productRows: [
                {
                    productName: '',
                    basePrice: '',
                    productTemp: null,
                    productSize: null,
                    productCategory: null,
                    productStation: null,
                    imageFile: null,      // Store the actual file
                    imagePreview: null,    // Store preview URL
                    hasImage: false,       // Track if image is present
                },
            ],
        };
    },

    setup() {
        const productsStore = useProductsStore();
        const productOptionsStore = useProductOptionsStore();
        return {
            productsStore,
            productOptionsStore,
            productTemperatureOption: computed(() => productOptionsStore.temperatureOptions),
            productSizeOption: computed(() => productOptionsStore.sizeOptions),
            productCategoryOption: computed(() => productOptionsStore.baseCategoryOptions),
            productStationOption: computed(() => productOptionsStore.stationOptions),
        };
    },

    created() {
        this.shopID = this.$route.query.shop_id;
        this.branchID = this.$route.query.branch_id;
        this.branchName = this.$route.query.branch_name;
    },

    mounted() {
        this.fetchProductOptions();
    },

    computed: {
        isFormValid() {
            return this.productRows.every(row => {
                return (
                    row.productName &&
                    row.productName.trim() !== '' &&
                    !isNaN(parseFloat(row.basePrice)) &&
                    parseFloat(row.basePrice) > 0 &&
                    row.productTemp &&
                    row.productSize &&
                    row.productCategory &&
                    row.productStation
                );
            });
        },
    },

    methods: {

        back() {
            this.$router.go(-1);
        },

        async fetchProductOptions() {
            await this.productOptionsStore.fetchAllOptions();
        },

        triggerFileUpload(index) {
            const fileInput = this.fileInputRefs[index];
            if (fileInput) {
                fileInput.click();
            }
        },

        handleFileUpload(event, index) {
            const file = event.target.files[0];
            if (!file) return;

            // Validate file size (max 2MB as per UI)
            if (file.size > 2 * 1024 * 1024) {
                this.showError('Image must be less than 2MB');
                return;
            }

            // Validate file type
            const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
            if (!allowedTypes.includes(file.type)) {
                this.showError('Only JPG, PNG, or WebP images are allowed');
                return;
            }

            // Store the file
            this.productRows[index].imageFile = file;
            this.productRows[index].hasImage = true;

            // Create preview
            const reader = new FileReader();
            reader.onload = (e) => {
                this.productRows[index].imagePreview = e.target.result;
            };
            reader.readAsDataURL(file);
        },

        removeImage(index) {
            this.productRows[index].imageFile = null;
            this.productRows[index].imagePreview = null;
            this.productRows[index].hasImage = false;

            // Clear the file input
            const fileInput = this.fileInputRefs[index];
            if (fileInput) {
                fileInput.value = '';
            }
        },

        removeRow(index) {
            if (this.productRows.length > 1) {
                // Clean up preview URL to avoid memory leaks
                if (this.productRows[index].imagePreview) {
                    URL.revokeObjectURL(this.productRows[index].imagePreview);
                }
                this.productRows.splice(index, 1);
                // Re-index file input refs
                this.updateFileInputRefs();
            }
        },

        updateFileInputRefs() {
            // Create new refs object
            const newRefs = {};
            this.productRows.forEach((_, idx) => {
                if (this.fileInputRefs[idx]) {
                    newRefs[idx] = this.fileInputRefs[idx];
                }
            });
            this.fileInputRefs = newRefs;
        },

        showSubmitDialog() {
            if (this.isFormValid) {
                this.submitDialog = true;
            } else {
                this.showError('Please fill in all required fields');
            }
        },

        closeSubmitDialog() {
            this.submitDialog = false;
        },

        addRow() {
            this.productRows.push({
                productName: '',
                basePrice: '',
                productTemp: null,
                productSize: null,
                productCategory: null,
                productStation: null,
                imageFile: null,
                imagePreview: null,
                hasImage: false,
            });
        },

        async submitForm() {
            this.submitDialog = false;

            // Validate form again before submission
            if (!this.isFormValid) {
                this.showError('Please fill in all required fields');
                return;
            }

            try {
                this.validatingProduct = true;

                // Create FormData for multipart upload
                const formData = new FormData();

                // Track if we have any products to save
                let validProductsCount = 0;

                this.productRows.forEach((row, index) => {
                    // Only add products with required fields
                    if (row.productName && row.basePrice && row.productTemp &&
                        row.productSize && row.productCategory && row.productStation) {

                        validProductsCount++;

                        formData.append(`products[${index}][product_name]`, row.productName.trim());
                        formData.append(`products[${index}][base_price]`, parseFloat(row.basePrice.replace(/[^0-9.]/g, '')) || 0);
                        formData.append(`products[${index}][size_id]`, row.productSize);
                        formData.append(`products[${index}][temp_id]`, row.productTemp);
                        formData.append(`products[${index}][category_id]`, row.productCategory);
                        formData.append(`products[${index}][station_id]`, row.productStation);
                        formData.append(`products[${index}][branch_id]`, Number(this.branchID));

                        // Append image ONLY if it exists
                        if (row.hasImage && row.imageFile) {
                            formData.append(`products[${index}][image]`, row.imageFile);
                        }
                    }
                });

                if (validProductsCount === 0) {
                    this.showError('No valid products to save');
                    this.validatingProduct = false;
                    return;
                }

                const response = await this.productsStore.saveProductsStore(formData);

                if (response && response.status === true) {
                    this.showSuccess(response.message);

                    // Reset form
                    this.$refs.productForm?.reset();
                    this.productRows = [
                        {
                            productName: '',
                            basePrice: '',
                            productTemp: null,
                            productSize: null,
                            productCategory: null,
                            productStation: null,
                            imageFile: null,
                            imagePreview: null,
                            hasImage: false,
                        },
                    ];
                    this.fileInputRefs = {};
                }
            } catch (error) {
                console.error('Submit error:', error);
                const errorMessage = error.response?.data?.message || error.message || 'Failed to save products';
                this.showError(errorMessage);
            } finally {
                this.validatingProduct = false;
            }
        },

        showError(message) {
            if (this.$refs.snackbarRef) {
                this.$refs.snackbarRef.showSnackbar(message, "error");
            } else {
                alert(message);
            }
        },

        showSuccess(message) {
            if (this.$refs.snackbarRef) {
                this.$refs.snackbarRef.showSnackbar(message, "success");
            } else {
                alert(message);
            }
        },
    },

    beforeUnmount() {
        // Clean up all preview URLs
        this.productRows.forEach(row => {
            if (row.imagePreview && row.imagePreview.startsWith('blob:')) {
                URL.revokeObjectURL(row.imagePreview);
            }
        });
    },

};
</script>

<style scoped>
.parent {
    display: flex;
    flex-wrap: wrap;
}

.child {
    width: 100%;
    max-width: 350px;
    margin-left: 4px;
    margin-right: 4px;
}

.image-upload-container {
    height: 100px;
    max-width: 400px;
    cursor: pointer;
    overflow: hidden;
    object-fit: cover;
    margin-bottom: 20px;
}

.image-preview {
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #ddd;
    border-radius: 8px;
}

.preview-img {
    width: 80px;
    height: 80px;
    object-fit: cover;
    border-radius: 4px;
}

.remove-image-btn {
    position: absolute;
    top: 8px;
    right: 8px;
    min-width: 24px;
    width: 24px;
    height: 24px;
    padding: 0;
}

.upload-placeholder {
    width: 100%;
    height: 100%;
    border: 2px dashed #ccc;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    background: #f9f9f9;
    transition: all 0.3s ease;
    padding: 8px;
}

.upload-placeholder:hover {
    border-color: #4CAF50;
    background: #f0f9f0;
}

.text-caption {
    font-size: 11px;
    margin: 2px 0;
}
</style>